import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type { LoginResponse } from '@/types/residents';

export interface ILogin {
  email: string;
  password: string;
}

export interface IGoogleLogin {
  credential: string;
  isAdmin: boolean;
}

export interface IAppleLogin {
  identityToken: string;
  isAdmin: boolean;
  user?: {
    email?: string;
    fullName?: {
      givenName?: string;
      familyName?: string;
    };
  };
}

export interface ICreateAccount {
  email: string;
  type?: number; // 1 = resident, 2 = guarantor/parent
}

// Use shared LoginResponse shape: { success, response: { accessToken, user } }

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'Auth'],
  endpoints: (builder) => ({
    deleteAccount: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: '/api/v1/users/delete-account',
        method: 'POST',
      }),
    }),
    // Admin login (v3) using Basic Auth header, cookie refresh enabled
    adminLogin: builder.mutation<LoginResponse, { email: string; password: string }>({
      query: ({ email, password }) => {
        const basic = typeof btoa !== 'undefined' ? btoa(`${email}:${password}`) : Buffer.from(`${email}:${password}`).toString('base64');
        return {
          url: '/api/v3/login?isAdmin=true&isMobile=true',
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: `Basic ${basic}`,
            'Content-Type': 'application/json',
          },
          body: {},
        } as any;
      },
      invalidatesTags: ['User', 'Auth'],
    }),

    login: builder.mutation<LoginResponse, ILogin>({
      query: (credentials) => ({
        url: '/api/v2/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['User', 'Auth'],
    }),

    googleLogin: builder.mutation<LoginResponse, IGoogleLogin>({
      query: (googleData) => ({
        url: '/api/v1/auth/google/google-login',
        method: 'POST',
        body: googleData,
        timeout: 120000, // 120 seconds (2 minutes) - Admin Google login can be slow due to:
        // 1. Google token verification
        // 2. Complex database queries for admin roles/permissions
        // 3. Loading organization and property data
        // 4. prepareSuperAdminPerms() processing
      }),
      invalidatesTags: ['User', 'Auth'],
    }),

    appleLogin: builder.mutation<LoginResponse, IAppleLogin>({
      query: (appleData) => ({
        url: '/api/v1/auth/apple/apple-login',
        method: 'POST',
        body: appleData,
        timeout: 120000, // 120 seconds (2 minutes) - Same timeout as Google login
      }),
      invalidatesTags: ['User', 'Auth'],
    }),

    createAccount: builder.mutation<{ success: boolean; message?: string }, ICreateAccount>({
      query: (accountData) => ({
        url: '/api/v2/users/account',
        method: 'POST',
        body: accountData,
      }),
    }),

    forgotPassword: builder.mutation<{ success: boolean; message?: string }, { email: string }>({
      query: (data) => ({
        url: '/api/v1/auth/forgot-password',
        method: 'POST',
        body: data,
      }),
    }),

    sendVerificationEmail: builder.mutation<{ success: boolean; message?: string }, { email: string; targetPath?: string }>({
      query: (data) => ({
        url: '/api/v2/users/verification',
        method: 'POST',
        body: data,
      }),
    }),

    verifyEmail: builder.mutation<{ success: boolean; message?: string }, { email: string; code: string }>({
      query: (data) => ({
        url: '/api/v2/users/verification',
        method: 'POST',
        body: data,
      }),
    }),

    setPassword: builder.mutation<{ success: boolean; message?: string }, { token: string; password: string }>({
      query: (data) => ({
        url: '/api/v2/users/set-password',
        method: 'POST',
        body: data,
      }),
    }),

    // Cookie-based logout v2
    adminLogout: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: '/api/v2/logout',
        method: 'POST',
        credentials: 'include',
      }),
      invalidatesTags: ['User', 'Auth'],
    }),
  }),
});

export const {
  useAdminLoginMutation,
  useLoginMutation,
  useGoogleLoginMutation,
  useAppleLoginMutation,
  useCreateAccountMutation,
  useForgotPasswordMutation,
  useSendVerificationEmailMutation,
  useVerifyEmailMutation,
  useSetPasswordMutation,
  useAdminLogoutMutation,
  useDeleteAccountMutation,
} = authApi;


